import React, { createElement } from 'react';
import { observer, inject } from 'mobx-react';
import { isObservableMap } from 'mobx';
import {
  mobxPluginFactory as mobxPlugin,
  RouterStore,
} from 'core/routing/store';
import { createRouter } from 'router5';
import loggerPlugin from 'router5-plugin-logger';
import browserPlugin from 'router5-plugin-browser';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import objectPath from 'object-path';

import { fromLocalStorage } from 'core/localStorage';
import { analytics } from 'core/analytics';
import { inWhitelabel, isPathExcluded, whitelabelMap } from 'core/whitelabel';

// Instantiate it directly or extend the class as you wish before invoking new
export const routerStore = new RouterStore();

routerStore.navigateNext = (fallbackNext = { name: 'home' }) => {
  /* Navigate to the next parameter if provided or 'fallbackNext' */
  const nextParam = routerStore.params.get('next');
  const next = nextParam
    ? routerStore.router.matchPath(nextParam) || fallbackNext
    : fallbackNext;
  routerStore.navigate(next.name, next.params, { replace: true });
};

// eslint-disable-next-line consistent-return
export const goBack = () => {
  const { route, router, previousRoute } = routerStore;
  const name = previousRoute
    ? previousRoute.name
    : route.name.split('.').slice(0, -1).join('.');
  const params = previousRoute ? previousRoute.params : route.params;
  if (name) {
    return router.navigate(name, params);
  }
  window.history.go(-1);
};

export const disableLeaving = callback => {
  // eslint-disable-next-line no-restricted-globals
  history.pushState(null, null, null);
  routerStore.leaveCallback = callback;
};

export const allowLeaving = () => {
  routerStore.leaveCallback = null;
};

const preventLeavingBack = () => {
  const leavePage = routerStore.leaveCallback();
  if (leavePage) {
    allowLeaving();
    // eslint-disable-next-line no-restricted-globals
    history.back();
  } else {
    // eslint-disable-next-line no-restricted-globals
    history.pushState(null, null, null);
  }
};

const preventLeavingLink = toState => {
  const leavePage = routerStore.leaveCallback();
  if (leavePage) {
    allowLeaving();
    routerStore.router.navigate(toState.name, toState.params, {
      replace: true,
    });
  }
  // FIXME - calling cancel on undefined router to trigger and exception and effectively cancel navigation.
  // eslint-disable-next-line
  router.cancel();
};

const onPopState = () => {
  if (routerStore.leaveCallback) {
    preventLeavingBack();
  }
  routerStore.isPop = true;
};

const scrollTop = () => ({
  onStart: () => {
    window.addEventListener('popstate', onPopState);
  },
  onStop: () => {
    window.removeEventListener('popstate', onPopState);
  },
  onTransitionStart: toState => {
    if (routerStore.leaveCallback) {
      preventLeavingLink(toState);
    }
  },
  onTransitionSuccess: (toState, fromState) => {
    const sameBaseUrl =
      objectPath.get(toState, 'name', 'to') ===
      objectPath.get(fromState, 'name', 'from');
    window.requestAnimationFrame(() => {
      if (!(sameBaseUrl || routerStore.isPop)) {
        window.scrollTo?.(0, 0);
      }
      routerStore.isPop = false;
    });
  },
});
scrollTop.pluginName = 'SCROLL_TOP';

const ga = () => {
  return {
    onTransitionSuccess: toState => {
      analytics.sendEvent(
        'page-view',
        { url: toState.path },
        { ga: true, intercom: true, pixel: false },
      );
    },
  };
};
ga.pluginName = 'GOOGLE_ANALYTICS';

// eslint-disable-next-line consistent-return
const whitelabelMiddleware = router => (toState, fromState, done) => {
  if (isPathExcluded(toState.name)) {
    const relativePath = router.buildPath(toState.name, toState.params);
    window.location.href = MAIN_URL + relativePath;
    return false;
  }
  done();
};

const intercomMiddleware = () => (toState, fromState, done) => {
  // FIXME - generalize this to a list if it grows, remove middleware if this is not needed anymore
  analytics.setIntercomVisible(!(toState?.name === 'viewer'));
  done();
};

export function configureRouter(
  routes,
  { useLoggerPlugin = false, defaultRoute = null } = {},
) {
  const opts = {
    trailingSlashMode: 'default',
    allowNotFound: true,
    queryParamsMode: 'loose',
    queryParams: {
      nullFormat: 'hidden',
      booleanFormat: 'string',
      arrayFormat: 'index',
    },
  };
  if (defaultRoute) {
    opts.defaultRoute = defaultRoute;
  }
  const router = createRouter(routes, opts);
  router.usePlugin(
    browserPlugin({ preserveHash: true, forceDeactivate: false }),
  );
  router.usePlugin(mobxPlugin(routerStore));
  router.usePlugin(scrollTop);
  router.usePlugin(ga);

  const wpRoutes = [
    'home.pricing',
    'home.privacy',
    'home.cookies',
    'home.terms',
    'home.workspace',
    'home.marketplace',
    'home.company',
  ];

  const wpHandler = (toState, fromState) => {
    if (fromState !== null) {
      window.location.href = toState.path;
      return Promise.reject();
    }
    return true;
  };

  router.canActivate('home', () => (...args) => {
    if (!fromLocalStorage('signedInProfile')) {
      return wpHandler(...args);
    }
    return true;
  });

  wpRoutes.forEach(routeName => {
    router.canActivate(routeName, () => wpHandler);
  });

  if (useLoggerPlugin) {
    router.usePlugin(loggerPlugin);
  }
  if (inWhitelabel()) {
    router.useMiddleware(whitelabelMiddleware);
  }
  router.useMiddleware(intercomMiddleware);
  router.start();
  return router;
}

export const getDisplayName = component =>
  component.displayName || component.name || 'Component';

export const ifNot = (condition, errorMessage) => {
  if (!condition) throw new Error(errorMessage);
};

// TODO: create another wrapper function to pass a custom store name
export function routeNode(nodeName, componentMap = {}, fourOhFour = false) {
  // route node Name
  return function routeNodeWrapper(RouteSegment) {
    // component Name
    @inject('routerStore')
    @observer
    class RouteNode extends React.Component {
      render() {
        const { props } = this;
        const component = createElement(RouteSegment, {
          ...props,
          nodeName,
          route: props.routerStore.route,
          componentFromRoute: this.componentFromRoute,
        });

        return component;
      }

      componentFromRoute = () => {
        const { route } = this.props.routerStore;
        const segments = route.name.split('.');
        const current = nodeName.split('.');
        // eslint-disable-next-line no-restricted-syntax
        for (const seg of current) {
          if (segments[0] === seg) {
            segments.shift();
          }
        }
        let componentKey = '';
        if (segments.length) {
          const joinedSegments = segments.join('.');
          const paths = Object.keys(componentMap).filter(e => !!e);
          componentKey =
            paths.find(path => joinedSegments.match(RegExp(`^${path}$`))) ||
            paths.find(path => joinedSegments.match(RegExp(`^${path}`))) ||
            segments[0];
        }
        return componentMap[componentKey] || fourOhFour;
      };
    }
    RouteNode.propTypes = {
      routerStore: PropTypes.object,
    };
    RouteNode.displayName = `RouteNode[${getDisplayName(RouteSegment)}]`;
    return RouteNode;
  };
}

@inject('routerStore')
@observer
export class BaseLink extends React.Component {
  buildUrl(name, params) {
    if (isObservableMap(params)) {
      params = params.toJS();
    }
    // If browser plugin is active
    if (this.props.routerStore.router.buildUrl) {
      return this.props.routerStore.router.buildUrl(name, params);
    }
    return this.props.routerStore.router.buildPath(name, params);
  }

  isActive = () =>
    this.props.routerStore.router.isActive(
      this.props.name,
      this.props.params,
      this.props.activeStrict,
    );

  clickHandler = evt => {
    if (this.props.onClick) {
      this.props.onClick(evt);

      if (evt.defaultPrevented) {
        return;
      }
    }
    // FIXME - Move Link build to a lifecycle method on props change
    const { name, params, content, target } = this.props;
    const href = this.buildLink(name, params);
    let baseURL = MAIN_URL;
    if (content) {
      const whitelabelData = whitelabelMap[content.whitelabel];
      if (whitelabelData) {
        const whitelabelURL = whitelabelData.url
          ? whitelabelData.url
          : whitelabelData;
        baseURL = whitelabelURL || baseURL;
      }
    }
    try {
      const linkURL = new URL(href, baseURL);
      const currentURL = window.location;
      if (currentURL.host !== linkURL.host) {
        return;
      }
    } catch (err) {
      console.error(err);
    }
    const comboKey = evt.metaKey || evt.altKey || evt.ctrlKey || evt.shiftKey;

    const isRegularClick = evt.button === 0 && !comboKey;
    const isConfiguredToOpenInTab = target === '_blank';
    const shouldNavigateCurrentTab = isRegularClick && !isConfiguredToOpenInTab;

    if (shouldNavigateCurrentTab) {
      // custom router code that needs to be aware of navigations
      evt.preventDefault();
      try {
        this.props.routerStore.router.navigate(
          this.props.name,
          this.props.params,
          this.props.routeOptions,
        );
      } catch (e) {
        console.warn(
          `Error navigating to url for route "${
            this.props.name
          }" with params "${JSON.stringify(this.props.params)}"`,
        );
      }
    }
  };

  buildLink(name, params) {
    let href = '';
    try {
      href = this.buildUrl(name, params);
    } catch (e) {
      console.warn(
        `Error building url for route "${name}" with params "${JSON.stringify(
          params,
        )}"`,
      );
    }
    if (params.fragment) {
      return `${href}#${params.fragment}`;
    }
    return href;
  }

  render() {
    const {
      name,
      params,
      className,
      activeClassName,
      children,
      title,
      onMouseOver,
      content,
      draggable,
      target,
      style,
    } = this.props;
    const active = this.isActive();
    const linkclassName = (className ? className.split(' ') : [])
      .concat(active ? [activeClassName] : [])
      .join(' ');
    let href = this.buildLink(name, params);
    if (content) {
      const whitelabelData = whitelabelMap[content.whitelabel];
      if (whitelabelData) {
        const whitelabelURL = whitelabelData.url
          ? whitelabelData.url
          : whitelabelData;
        href = whitelabelURL + href;
      } else {
        href = MAIN_URL + href;
      }
    }
    const props = {
      href,
      className: linkclassName,
      onClick: this.clickHandler,
      onMouseOver,
      title,
      draggable,
      target,
      style,
    };
    return <a {...props}>{children}</a>;
  }
}

// TODO: Export directly Link instead of BaseLink ??
export const Link = styled(BaseLink)``;

@observer
export class ExternalLink extends React.Component {
  render() {
    let { to } = this.props;
    const { children, noBlank = false } = this.props;
    if (!!to && to.indexOf('://') === -1 && !to.startsWith('/')) {
      to = `//${to}`;
    }
    return (
      <a target={noBlank ? '_self' : '_blank'} rel='noopener' href={to}>
        {children}
      </a>
    );
  }
}

ExternalLink.propTypes = {
  to: PropTypes.string,
  children: PropTypes.node,
  noBlank: PropTypes.bool,
};

BaseLink.propTypes = {
  name: PropTypes.string.isRequired,
  params: PropTypes.object.isRequired,
  className: PropTypes.string,
  activeStrict: PropTypes.bool,
  activeClassName: PropTypes.string,
  children: PropTypes.node,
  onClick: PropTypes.func,
  onMouseOver: PropTypes.func,
  routeOptions: PropTypes.object,
  routerStore: PropTypes.object,
  title: PropTypes.string,
  content: PropTypes.object,
  draggable: PropTypes.bool,
  target: PropTypes.string,
  style: PropTypes.object,
};

BaseLink.defaultProps = {
  activeClassName: 'active',
  activeStrict: false,
  name: '',
  params: {},
  routeOptions: {},
};
